import { useEffect, useState } from "react";
import { lotteryData, lotteryKeys, lotteryLabels } from "@/lib/lottery-data";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface LiveData {
  title: string;
  balls: { n: string; c?: "warm" | "cool" }[];
  resultNote: string;
  bars: { n: string; v: string; w: number; c: "g" | "a" | "" }[];
  dna: { k: string; v: string; hi?: boolean }[];
}

const PRIZE_TIERS: Record<string, number[]> = {
  "mega-sena": [6, 5, 4, 3, 2, 1],
  lotofacil: [15, 14, 13, 12, 11, 10],
  quina: [5, 4, 3, 2, 1, 0],
  "dupla-sena": [6, 5, 4, 3, 2, 1],
};

const LotterySection = () => {
  const [active, setActive] = useState<string>("mega-sena");
  const [live, setLive] = useState<LiveData | null>(null);
  const [loading, setLoading] = useState(true);
  const fallback = lotteryData[active];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // 1. Lottery
      const { data: lottery } = await supabase
        .from("lotteries")
        .select("id, name, numbers_drawn")
        .eq("slug", active)
        .single();
      if (!lottery || cancelled) {
        setLive(null);
        setLoading(false);
        return;
      }

      // 2. Latest draw
      const { data: latest } = await supabase
        .from("draws")
        .select("id, contest_number, draw_date")
        .eq("lottery_id", lottery.id)
        .order("contest_number", { ascending: false })
        .limit(1)
        .single();
      if (!latest || cancelled) {
        setLive(null);
        setLoading(false);
        return;
      }

      // 3. Numbers of latest draw
      const { data: latestNums } = await supabase
        .from("draw_numbers")
        .select("number")
        .eq("draw_id", latest.id)
        .order("number", { ascending: true });
      const drawnNumbers = (latestNums ?? []).map((n) => n.number);

      // 4. Number stats (frequency + delay)
      const { data: stats, error: statsError } = await supabase.rpc("get_number_stats", {
        _lottery_slug: active,
        _last_n: 100,
      });
      const statsArr = statsError ? [] : (stats ?? []);
      const byNum = new Map(statsArr.map((s) => [s.number, s]));
      const maxFreq = Math.max(1, ...statsArr.map((s) => Number(s.frequency)));
      // Frequência das DEZENAS do último concurso (em ordem decrescente de frequência)
      const drawnStats = drawnNumbers
        .map((n) => byNum.get(n))
        .filter(Boolean) as typeof statsArr;
      const drawnFreqsSorted = [...drawnStats].sort(
        (a, b) => Number(b.frequency) - Number(a.frequency),
      );
      const bars = drawnFreqsSorted.slice(0, 5).map((s, i) => ({
        n: String(s.number).padStart(2, "0"),
        v: `${s.frequency}×`,
        w: Math.round((Number(s.frequency) / maxFreq) * 100),
        c: (i < 2 ? "g" : i < 3 ? "a" : "") as "g" | "a" | "",
      })) as LiveData["bars"];

      // 5. Result note: sum + parity + hottest/coldest of drawn numbers
      const sum = drawnNumbers.reduce((a, b) => a + b, 0);
      const evens = drawnNumbers.filter((n) => n % 2 === 0).length;
      const odds = drawnNumbers.length - evens;
      const drawnByDelay = [...drawnNumbers]
        .map((n) => ({ n, s: byNum.get(n) }))
        .filter((x) => x.s);
      const hottest = drawnByDelay.length > 0
        ? drawnByDelay.reduce((a, b) =>
            Number(a.s!.frequency) >= Number(b.s!.frequency) ? a : b,
          )
        : null;
      const longestDelay = drawnByDelay.length > 0
        ? drawnByDelay.reduce((a, b) =>
            (a.s!.delay ?? 0) >= (b.s!.delay ?? 0) ? a : b,
          )
        : null;
      const resultNote =
        `Soma: ${sum} · ${evens} pares / ${odds} ímpares` +
        (hottest && longestDelay
          ? `<br/>${String(hottest.n).padStart(2, "0")} em alta · ${String(longestDelay.n).padStart(2, "0")} retornando após ${longestDelay.s!.delay} ausências`
          : "");

      // 6. Mark hot/cold balls (top 25% / bottom 25% by frequency in last 100)
      const freqLookup = new Map(statsArr.map((s) => [s.number, Number(s.frequency)]));
      const allFreqs = [...freqLookup.values()].sort((a, b) => a - b);
      const hotThr = allFreqs[Math.floor(allFreqs.length * 0.75)] ?? Infinity;
      const coldThr = allFreqs[Math.floor(allFreqs.length * 0.25)] ?? -Infinity;
      const balls = drawnNumbers.map((n) => {
        const f = freqLookup.get(n) ?? 0;
        const c: "warm" | "cool" | undefined =
          f >= hotThr ? "warm" : f <= coldThr ? "cool" : undefined;
        return { n: String(n).padStart(2, "0"), c };
      });

      // 7. DNA do jogo do ÚLTIMO concurso — quantas vezes essas dezenas
      // teriam acertado cada faixa em todo o histórico.
      const tiers = PRIZE_TIERS[active] ?? [6, 5, 4, 3, 2, 1];
      const topTier = Math.max(...tiers);
      const { data: dnaData } = await supabase.rpc("compute_dna", {
        _lottery_slug: active,
        _numbers: drawnNumbers,
      });
      const dnaObj = dnaData as {
        histogram?: Array<{ hits: number; count: number }>;
        sorteios?: Array<{ histogram: Array<{ hits: number; count: number }> }>;
        is_dupla_sena?: boolean;
      } | null;
      // Para Dupla Sena soma os dois sorteios; para as demais usa o histograma único.
      const byHits = new Map<number, number>();
      if (dnaObj?.is_dupla_sena && dnaObj.sorteios?.length) {
        for (const s of dnaObj.sorteios) {
          for (const h of s.histogram ?? []) {
            byHits.set(h.hits, (byHits.get(h.hits) ?? 0) + h.count);
          }
        }
      } else {
        for (const h of dnaObj?.histogram ?? []) {
          byHits.set(h.hits, h.count);
        }
      }
      const dna = tiers.map((t) => {
        const c = byHits.get(t);
        return {
          k: `${t} acertos`,
          v: `${(c ?? 0).toLocaleString("pt-BR")}×`,
          hi: t >= topTier - 1,
        };
      });

      if (cancelled) return;
      setLive({
        title: `${lottery.name} · Concurso ${latest.contest_number}`,
        balls,
        resultNote,
        bars,
        dna,
      });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [active]);

  const d = {
    ...fallback,
    title: live?.title ?? fallback.title,
    balls: live?.balls ?? fallback.balls,
    resultNote: live?.resultNote ?? fallback.resultNote,
    bars: live?.bars ?? fallback.bars,
    dna: live?.dna ?? fallback.dna,
  };
  const labels = ["Universo", "Chance máxima", "Jogo mínimo", "Preço base", "Prêmio médio", "Sorteios"];

  return (
    <div className="mx-auto max-w-[900px] px-8 pb-[72px]">
      <div className="mb-3 text-[10px] font-medium uppercase tracking-[0.14em] text-primary">Cobertura completa</div>
      <h2 className="mb-3.5 font-serif text-[clamp(22px,3.2vw,42px)] font-light leading-[1.15] text-foreground whitespace-nowrap">
        Quatro loterias, <em className="text-primary italic">dados de verdade</em>
      </h2>
      <p className="mb-9 max-w-[500px] text-[15px] leading-relaxed text-lt-cream3">
        Mega Sena, Lotofácil, Quina e Dupla Sena — histórico, estatísticas e probabilidades reais
      </p>

      {/* Tabs */}
      <div className="mb-5 flex flex-wrap gap-1.5">
        {lotteryKeys.map((key) => (
          <button
            key={key}
            onClick={() => setActive(key)}
            className={cn(
              "rounded-md border px-[18px] py-2 font-sans text-xs tracking-wider transition-colors",
              active === key
                ? "border-primary bg-primary font-medium text-primary-foreground"
                : "border-lt-border bg-transparent text-lt-cream3 hover:border-lt-cream3"
            )}
          >
            {lotteryLabels[key]}
          </button>
        ))}
      </div>

      {/* Info Panel */}
      <div className="rounded-2xl border border-lt-border bg-lt-bg2 p-6 transition-colors">
        <div className="mb-5 grid grid-cols-[repeat(auto-fit,minmax(100px,1fr))] gap-2.5">
          {labels.map((label, i) => (
            <div key={i} className="rounded-lg border border-lt-border2 bg-lt-bg3 p-3 transition-colors">
              <div className="mb-1 text-[9px] font-medium uppercase tracking-[0.12em] text-lt-cream3">{label}</div>
              <div className={cn("font-serif text-[17px] text-foreground", (i === 1 || i === 4) && "text-primary")}>
                {d.vals[i]}
              </div>
            </div>
          ))}
        </div>
        <div
          className="rounded-r-lg border-l-2 border-l-primary bg-lt-bg3 px-4 py-3.5 text-xs leading-relaxed text-lt-cream3 transition-colors"
          dangerouslySetInnerHTML={{ __html: d.note }}
        />
      </div>

      {/* Data Panel */}
      <div className="mt-[72px] overflow-hidden rounded-2xl border border-lt-border bg-lt-bg2 transition-colors">
        <div className="flex items-center justify-between border-b border-lt-border bg-lt-bg3 px-5 py-3 transition-colors">
          <div className="font-sans text-[11px] tracking-wider text-lt-cream3">
            LotoTerminal — <span className="font-medium text-primary">{loading ? "Carregando…" : d.title}</span>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3.5 p-5 md:grid-cols-3">
          {/* Last Result */}
          <div className="rounded-lg border border-lt-border2 bg-lt-bg3 p-4 transition-colors">
            <div className="mb-2.5 text-[10px] font-medium uppercase tracking-[0.1em] text-lt-cream3">Último resultado</div>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {d.balls.map((b, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex h-[30px] w-[30px] items-center justify-center rounded-full border text-[11px] font-medium transition-colors",
                    b.c === "warm" && "border-lt-gold-border bg-lt-gold-bg text-primary",
                    b.c === "cool" && "border-lt-teal-border bg-lt-teal-bg text-lt-teal",
                    !b.c && "border-lt-border bg-lt-bg4 text-lt-cream2"
                  )}
                >
                  {b.n}
                </div>
              ))}
            </div>
            <div className="text-[11px] leading-relaxed text-lt-cream3" dangerouslySetInnerHTML={{ __html: d.resultNote }} />
          </div>

          {/* Frequency bars */}
          <div className="rounded-lg border border-lt-border2 bg-lt-bg3 p-4 transition-colors">
            <div className="mb-2.5 text-[10px] font-medium uppercase tracking-[0.1em] text-lt-cream3">Frequência das dezenas</div>
            {d.bars.map((b, i) => (
              <div key={i} className="mb-2">
                <div className="mb-0.5 flex justify-between text-[10px] text-lt-cream3">
                  <span>{b.n}</span>
                  <span>{b.v}</span>
                </div>
                <div className="h-1 overflow-hidden rounded-sm bg-lt-bg4 transition-colors">
                  <div
                    className={cn(
                      "h-full rounded-sm",
                      b.c === "g" && "bg-lt-teal",
                      b.c === "a" && "bg-primary",
                      !b.c && "bg-lt-cream3"
                    )}
                    style={{ width: `${b.w}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* DNA */}
          <div className="rounded-lg border border-lt-border2 bg-lt-bg3 p-4 transition-colors">
            <div className="mb-2.5 text-[10px] font-medium uppercase tracking-[0.1em] text-lt-cream3">DNA do jogo</div>
            {d.dna.map((r, i) => (
              <div key={i} className="flex justify-between border-b border-lt-border2 py-1 text-[11px] last:border-0">
                <span className="text-lt-cream3">{r.k}</span>
                <span className={cn("font-medium", r.hi ? "text-lt-teal" : "text-lt-cream2")}>{r.v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LotterySection;
